/**
 * Menu Customization Converter
 * 
 * Converts Branch data from database to MenuCustomization format
 */

import type { Branch } from '@/types/index';
import type {
  MenuCustomization,
  ColorScheme,
  TypographyConfig,
  SpacingConfig,
  SpacingScale,
  EffectsConfig,
  BorderRadius,
  Shadow,
  LayoutConfig,
  BackgroundConfig,
  HeaderCustomization,
  CategoryTabsCustomization,
  MenuCardCustomization,
  FloatingCartCustomization,
} from '@/types/menuCustomization';
import {
  DEFAULT_COLOR_SCHEME,
  DEFAULT_TYPOGRAPHY,
  DEFAULT_SPACING,
  DEFAULT_EFFECTS,
  DEFAULT_LAYOUT,
  DEFAULT_BACKGROUND,
} from '@/types/menuCustomization';
import type { GlobalThemeOverrides } from '@/types/menuBuilder';

/**
 * Converts a Branch object to a full MenuCustomization configuration
 */
export function branchToCustomization(branch: Branch): MenuCustomization {
  return {
    colors: extractColorScheme(branch),
    typography: extractTypography(branch),
    spacing: extractSpacing(branch),
    effects: extractEffects(branch),
    layout: extractLayout(branch),
    background: extractBackground(branch),
    components: {
      header: extractHeaderCustomization(branch),
      categoryTabs: extractCategoryTabsCustomization(branch),
      menuCard: extractMenuCardCustomization(branch),
      floatingCart: extractFloatingCartCustomization(branch),
    },
  };
}

/**
 * Extract color scheme from branch
 */
function extractColorScheme(branch: Branch): ColorScheme {
  return {
    primary: branch.primary_color || DEFAULT_COLOR_SCHEME.primary,
    secondary: branch.secondary_color || DEFAULT_COLOR_SCHEME.secondary,
    accent: branch.accent_color || DEFAULT_COLOR_SCHEME.accent,
    background: branch.background_color || DEFAULT_COLOR_SCHEME.background,
    surface: branch.surface_color || DEFAULT_COLOR_SCHEME.surface,
    text: branch.text_color || DEFAULT_COLOR_SCHEME.text,
    textSecondary: branch.text_secondary_color || DEFAULT_COLOR_SCHEME.textSecondary,
    border: branch.border_color || DEFAULT_COLOR_SCHEME.border,
    error: branch.error_color || DEFAULT_COLOR_SCHEME.error,
    success: branch.success_color || DEFAULT_COLOR_SCHEME.success,
  };
}

/**
 * Extract typography configuration from branch
 */
function extractTypography(branch: Branch): TypographyConfig {
  // Map font_family string to FontFamily type
  const fontFamilyMap: Record<string, TypographyConfig['fontFamily']> = {
    'Inter': 'inter',
    'Roboto': 'roboto',
    'Poppins': 'poppins',
    'Playfair Display': 'playfair',
    'Noto Sans Arabic': 'noto-sans-arabic',
    'Genty Sans': 'genty-sans',
  };

  // Map typography_scale to scale
  const scaleMap: Record<string, TypographyConfig['scale']> = {
    'xs': 'xs',
    'sm': 'sm',
    'normal': 'md',
    'lg': 'lg',
    'xl': 'xl',
  };

  // Map line_height
  const lineHeightMap: Record<string, TypographyConfig['lineHeight']> = {
    'tight': 'tight',
    'normal': 'normal',
    'relaxed': 'relaxed',
  };

  return {
    fontFamily: branch.font_family 
      ? (fontFamilyMap[branch.font_family] || 'inter')
      : DEFAULT_TYPOGRAPHY.fontFamily,
    scale: branch.typography_scale 
      ? (scaleMap[branch.typography_scale] || 'md')
      : DEFAULT_TYPOGRAPHY.scale,
    headingWeight: DEFAULT_TYPOGRAPHY.headingWeight,
    bodyWeight: DEFAULT_TYPOGRAPHY.bodyWeight,
    textShadow: DEFAULT_TYPOGRAPHY.textShadow,
    lineHeight: branch.line_height
      ? (lineHeightMap[branch.line_height] || 'normal')
      : DEFAULT_TYPOGRAPHY.lineHeight,
  };
}

/**
 * Extract spacing configuration from branch
 */
function extractSpacing(branch: Branch): SpacingConfig {
  const scaleMap: Record<string, SpacingScale> = {
    'dense': 'compact',
    'normal': 'normal',
    'relaxed': 'relaxed',
  };

  const spacing: SpacingScale = branch.spacing_mode 
    ? (scaleMap[branch.spacing_mode] || 'normal')
    : 'normal';

  return {
    cardGap: branch.item_gap === 'none' ? 'compact' : spacing,
    cardPadding: branch.container_padding === 'none' ? 'compact' : spacing,
    sectionSpacing: spacing,
    containerPadding: spacing,
  };
}

/**
 * Extract effects configuration from branch
 */
function extractEffects(branch: Branch): EffectsConfig {
  const radiusMap: Record<string, BorderRadius> = {
    'none': 'none',
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
    'xl': 'xl',
    'full': 'full',
  };

  const shadowMap: Record<string, Shadow> = {
    'none': 'none',
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
    'xl': 'xl',
    '2xl': '2xl',
  };

  const animationMap: Record<string, 'fast' | 'normal' | 'slow'> = {
    'fast': 'fast',
    'normal': 'normal',
    'slow': 'slow',
  };

  return {
    borderRadius: branch.card_border_radius
      ? (radiusMap[branch.card_border_radius] || 'lg')
      : DEFAULT_EFFECTS.borderRadius,
    shadow: branch.card_shadow
      ? (shadowMap[branch.card_shadow] || 'md')
      : DEFAULT_EFFECTS.shadow,
    borderWidth: DEFAULT_EFFECTS.borderWidth,
    hoverEffect: branch.hover_effect !== undefined 
      ? branch.hover_effect 
      : DEFAULT_EFFECTS.hoverEffect,
    transitionSpeed: branch.animation_speed
      ? (animationMap[branch.animation_speed] || 'normal')
      : DEFAULT_EFFECTS.transitionSpeed,
    glassMorphism: DEFAULT_EFFECTS.glassMorphism,
  };
}

/**
 * Extract layout configuration from branch
 */
function extractLayout(branch: Branch): LayoutConfig {
  const maxWidthMap: Record<string, LayoutConfig['maxWidth']> = {
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
    'xl': 'xl',
    '2xl': '2xl',
    'full': 'full',
  };

  const aspectRatioMap: Record<string, LayoutConfig['imageAspectRatio']> = {
    '1:1': 'square',
    '4:3': '4:3',
    '16:9': '16:9',
    '3:2': '3:2',
  };

  return {
    type: branch.menu_layout || DEFAULT_LAYOUT.type,
    maxWidth: branch.max_width
      ? (maxWidthMap[branch.max_width] || 'lg')
      : DEFAULT_LAYOUT.maxWidth,
    gridColumns: {
      mobile: (branch.grid_columns_mobile ?? DEFAULT_LAYOUT.gridColumns.mobile) as 1 | 2 | 3,
      tablet: (branch.grid_columns_tablet ?? DEFAULT_LAYOUT.gridColumns.tablet) as 1 | 2 | 3 | 4,
      desktop: (branch.grid_columns_desktop ?? DEFAULT_LAYOUT.gridColumns.desktop) as 1 | 2 | 3 | 4 | 5 | 6,
    },
    imageAspectRatio: branch.image_aspect_ratio
      ? (aspectRatioMap[branch.image_aspect_ratio] || 'square')
      : DEFAULT_LAYOUT.imageAspectRatio,
    showImages: DEFAULT_LAYOUT.showImages,
    imagePosition: DEFAULT_LAYOUT.imagePosition,
  };
}

/**
 * Extract background configuration from branch
 */
function extractBackground(branch: Branch): BackgroundConfig {
  const bgType = branch.background_type || 'none';
  
  if (bgType === 'none') {
    return DEFAULT_BACKGROUND;
  }
  
  // Handle different background types
  if (bgType === 'solid') {
    return {
      type: 'color',
      value: branch.background_color || DEFAULT_BACKGROUND.value,
      overlayOpacity: 0,
      blur: false,
      attachment: 'scroll',
    };
  }
  
  if (bgType === 'gradient') {
    const from = branch.background_gradient_from || '#f3f4f6';
    const to = branch.background_gradient_to || '#e5e7eb';
    const direction = branch.background_gradient_direction || 'to bottom';
    
    return {
      type: 'gradient',
      value: `linear-gradient(${direction}, ${from}, ${to})`,
      gradient: {
        type: 'linear',
        colors: [from, to],
      },
      overlayOpacity: 0,
      blur: false,
      attachment: 'scroll',
    };
  }
  
  if (bgType === 'image' && branch.background_image_url) {
    return {
      type: 'image',
      value: branch.background_image_url,
      overlayOpacity: 0,
      blur: false,
      attachment: 'scroll',
    };
  }
  
  if (bgType === 'pattern') {
    // Pattern backgrounds will be handled via CSS classes
    return {
      type: 'pattern',
      value: branch.background_pattern || 'dots',
      overlayOpacity: 0,
      blur: false,
      attachment: 'scroll',
    };
  }
  
  return DEFAULT_BACKGROUND;
}

/**
 * Extract header customization from branch
 */
function extractHeaderCustomization(branch: Branch): HeaderCustomization {
  const headerTypeMap: Record<string, HeaderCustomization['type']> = {
    'simple': 'simple',
    'with-cover': 'with-cover',
  };

  const logoSizeMap: Record<string, HeaderCustomization['logoSize']> = {
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
  };

  return {
    type: branch.header_variant
      ? (headerTypeMap[branch.header_variant] || 'with-cover')
      : (branch.cover_photo_url ? 'with-cover' : 'simple'),
    height: 'md',
    showLogo: true,
    logoSize: branch.logo_size
      ? (logoSizeMap[branch.logo_size] || 'md')
      : 'md',
    showDescription: true,
    showCoverPhoto: !!branch.cover_photo_url,
    coverPhotoOverlay: 50,
    sticky: false,
  };
}

/**
 * Extract category tabs customization from branch
 */
function extractCategoryTabsCustomization(branch: Branch): CategoryTabsCustomization {
  const iconStyleMap: Record<string, CategoryTabsCustomization['iconStyle']> = {
    'circle': 'circle',
    'square': 'square',
    'rounded': 'rounded',
  };

  const iconSizeMap: Record<string, CategoryTabsCustomization['size']> = {
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
  };

  return {
    type: 'horizontal',
    size: branch.category_icon_size
      ? (iconSizeMap[branch.category_icon_size] || 'md')
      : 'md',
    showIcons: branch.show_category_names !== false, // Show icons if names are shown
    iconStyle: branch.category_icon_variant
      ? (iconStyleMap[branch.category_icon_variant] || 'circle')
      : 'circle',
    sticky: branch.sticky_categories !== false,
    showItemCount: false,
    scrollToCategory: true,
  };
}

/**
 * Extract menu card customization from branch
 */
function extractMenuCardCustomization(branch: Branch): MenuCardCustomization {
  const priceStyleMap: Record<string, MenuCardCustomization['priceStyle']> = {
    'top': 'badge',
    'bottom': 'inline',
    'inline': 'inline',
  };

  return {
    showDescription: branch.show_item_description !== false,
    descriptionLines: (branch.description_lines && [1, 2, 3, 4].includes(branch.description_lines)
      ? branch.description_lines as 1 | 2 | 3 | 4
      : 2),
    priceStyle: branch.price_position
      ? (priceStyleMap[branch.price_position] || 'inline')
      : 'inline',
    showAvailability: true,
    showRatings: branch.enable_ratings || false,
    showBadges: branch.show_item_badges !== false,
    addButtonStyle: 'icon',
    hoverElevation: true,
  };
}

/**
 * Extract floating cart customization from branch
 */
function extractFloatingCartCustomization(branch: Branch): FloatingCartCustomization {
  const positionMap: Record<string, FloatingCartCustomization['position']> = {
    'bottom-left': 'bottom-left',
    'bottom-right': 'bottom-right',
    'bottom-center': 'bottom-center',
    'top-right': 'top-right',
  };

  const sizeMap: Record<string, FloatingCartCustomization['size']> = {
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
  };

  const animationMap: Record<string, FloatingCartCustomization['animation']> = {
    'none': 'none',
    'bounce': 'bounce',
    'pulse': 'pulse',
  };

  return {
    position: branch.cart_position
      ? (positionMap[branch.cart_position] || 'bottom-right')
      : 'bottom-right',
    size: branch.cart_size
      ? (sizeMap[branch.cart_size] || 'md')
      : 'md',
    showItemCount: true,
    showTotalAmount: true,
    autoHide: true,
    animation: branch.cart_animation
      ? (animationMap[branch.cart_animation] || 'bounce')
      : 'bounce',
  };
}

/**
 * Merge partial customization with defaults
 */
export function mergeCustomization(
  base: MenuCustomization,
  override: Partial<MenuCustomization>
): MenuCustomization {
  return {
    colors: { ...base.colors, ...override.colors },
    typography: { ...base.typography, ...override.typography },
    spacing: { ...base.spacing, ...override.spacing },
    effects: { ...base.effects, ...override.effects },
    layout: { ...base.layout, ...override.layout },
    background: { ...base.background, ...override.background },
    components: {
      header: { ...base.components.header, ...override.components?.header },
      categoryTabs: { ...base.components.categoryTabs, ...override.components?.categoryTabs },
      menuCard: { ...base.components.menuCard, ...override.components?.menuCard },
      floatingCart: { ...base.components.floatingCart, ...override.components?.floatingCart },
    },
  };
}

/**
 * Convert GlobalThemeOverrides to MenuCustomization partial
 */
export function overridesToCustomization(overrides: GlobalThemeOverrides): Partial<MenuCustomization> {
  const customization: Partial<MenuCustomization> = {
    components: {
      floatingCart: {} as any
    },
    typography: {} as any
  };

  if (overrides.fontFamily) {
    customization.typography!.fontFamily = overrides.fontFamily as any;
  }

  if (overrides.fontWeight) {
    customization.typography!.bodyWeight = overrides.fontWeight;
    // Also apply to headings if bold is selected, or keep them consistent
    customization.typography!.headingWeight = overrides.fontWeight === 'bold' ? 'bold' : 'normal'; 
  }

  if (overrides.textColor) {
    if (!customization.colors) customization.colors = {} as any;
    customization.colors!.text = overrides.textColor;
  }

  if (overrides.primaryColor) {
    if (!customization.colors) customization.colors = {} as any;
    customization.colors!.primary = overrides.primaryColor;
  }

  if (overrides.cartPosition) {
    customization.components!.floatingCart!.position = overrides.cartPosition;
  }
  
  if (overrides.cartButtonStyle) {
    customization.components!.floatingCart!.buttonStyle = overrides.cartButtonStyle;
  }
  
  if (overrides.cartButtonColor) {
    customization.components!.floatingCart!.buttonColor = overrides.cartButtonColor;
  }
  
  if (overrides.cartIconColor) {
    customization.components!.floatingCart!.iconColor = overrides.cartIconColor;
  }
  
  return customization;
}
